import getPackageRowName from 'components/menu/PackagePricing/PricingRow/utils';
import { findRepeatingSubSkuIdsInAllSkus } from 'components/menu/PackagePricing/utils';
import { calculatePrice } from 'components/menu/selectors';
import { withErrorHandling } from 'helpers/withErrorHandling';
import { track, TrackingProperties } from 'src/tracking/trackingService';

/**
 * Segment tracking for the Menu section (THY-2323 / THY-2347 / THY-2397).
 *
 * Event names are verbatim from the ticket — Segment event names are a public
 * contract with the dashboards built on them, so they are never reshaped here.
 *
 * Every exported tracker is wrapped in `withErrorHandling`: these are called
 * from save paths (the offer dialog reports before it closes itself), and an
 * analytics failure must never be what stops a partner's save from finishing.
 *
 * All 17 browser-fired events live in this one module, including the ones
 * called from Layer 1 Backbone views: those call a single function from here
 * and pass their model, so no analytics logic sits in Backbone. `track()`
 * already adds country, platform, venue_id, is_masquerading, supplier_id and
 * context.groupId, and no-ops unless the CD-176-segment-tracking flag is on.
 *
 * Payloads carry names as well as ids (THY-2397): reporting on menu content
 * without a developer to join ids to names is the whole point of the request,
 * and venue catalogue strings are already public on the marketplace. Client
 * data still never appears here — nothing in the Menu domain holds any.
 *
 * Property names follow analytics vocabulary, not `docs/GLOSSARY.md`, so that
 * they match the shipped event names. In particular
 * `treatment_type` is a Treatment while `treatment_category` is a treatment
 * *type*, which is the opposite nesting to what the words suggest.
 */

type MenuItemEntity = 'service' | 'package';
type SaveOperation = 'add_new' | 'edit' | 'archive';
type MenuGroupOperation = SaveOperation | 'delete';
type DiscountOperation = 'add_new' | 'edit' | 'delete';

const MENU_ITEM_INITIATED_EVENTS: Record<MenuItemEntity, string> = {
  service: 'menu.services.add_new.service.initiated',
  package: 'menu.services.add_new.package.initiated',
};

const MENU_ITEM_COMPLETED_EVENTS: Record<MenuItemEntity, Record<SaveOperation, string>> = {
  service: {
    add_new: 'menu.services.add_new.service.completed',
    edit: 'menu.services.edit.service.completed',
    archive: 'menu.services.archive.service.completed',
  },
  package: {
    add_new: 'menu.services.add_new.package.completed',
    edit: 'menu.services.edit.package.completed',
    archive: 'menu.services.archive.package.completed',
  },
};

/**
 * A deleted group reports under the archive event on purpose. THY-2323 gives
 * menu groups exactly one removal event and no `delete.menu_group.completed`
 * name, unlike pricing levels and discounts, so archive is the only place the
 * removal can land without inventing an 18th name outside the contract. Which
 * of the two the partner actually did is in `operation`: the modal offers
 * Archive for a group that still holds offers and Delete for an empty one.
 */
const MENU_GROUP_COMPLETED_EVENTS: Record<MenuGroupOperation, string> = {
  add_new: 'menu.services.add_new.menu_group.completed',
  edit: 'menu.services.edit.menu_group.completed',
  archive: 'menu.services.archive.menu_group.completed',
  delete: 'menu.services.archive.menu_group.completed',
};

const DISCOUNT_COMPLETED_EVENTS: Record<DiscountOperation, string> = {
  add_new: 'menu.discounts.add_new.discount.completed',
  edit: 'menu.discounts.edit.discount.completed',
  delete: 'menu.discounts.delete.discount.completed',
};

const MENU_GROUP_INITIATED_EVENT = 'menu.services.add_new.menu_group.initiated';
const PRICING_LEVEL_INITIATED_EVENT = 'menu.pricing_levels.add_new.pricing_level.initiated';
const DISCOUNT_INITIATED_EVENT = 'menu.discounts.add_new.discount.initiated';

/**
 * What we read off the offer model. Declared structurally rather than imported:
 * `App.Models.MenuOffer` is an untyped Layer 1 Backbone model, and spelling out
 * the handful of fields this module touches is what keeps the coupling visible.
 */
interface OfferSku {
  id?: number | null;
  name?: string | null;
  fullPriceAmount?: number | string | null;
  discountPriceAmount?: number | string | null;
  duration?: number | null;
  employeeCategoryId?: number | null;
  // Package rows only: the included services' own options, and whether the
  // partner offers this combination.
  subSkus?: OfferSku[];
  visible?: boolean | null;
}

interface OfferModelLike {
  id?: number | null;
  get: (key: string) => any;
  isServicePackage: () => boolean;
  isLegacyPackage: () => boolean;
  getPricingType?: () => string;
  attributesToSave?: () => string[];
  getTreatmentIds?: (offersCollection: any) => number[];
}

/**
 * Backbone collections the offer dialog already holds, passed in by the view so
 * that ids can be reported as the names partners actually recognise. Both are
 * optional: the dialog is reachable from routes that do not load them, and a
 * missing name is left out rather than guessed at.
 */
interface OfferTrackingContext {
  treatments?: any;
  fullMenu?: any;
  employeeCategories?: any;
}

export const getMenuItemEntity = (model: OfferModelLike): MenuItemEntity =>
  model.isServicePackage() || model.isLegacyPackage() ? 'package' : 'service';

const toFiniteNumber = (value: unknown): number | null => {
  const parsed = typeof value === 'string' ? parseFloat(value) : value;
  return typeof parsed === 'number' && isFinite(parsed) ? parsed : null;
};

const countUnique = (values: (number | null | undefined)[]): number =>
  values
    .filter((value) => value != null)
    .filter((value, index, all) => all.indexOf(value) === index).length;

const getCurrencyCode = (): string => {
  const currency = App.config && App.config.get('currency');
  return (currency && currency.currencyCode) || '';
};

const toSnakeCase = (key: string): string =>
  key.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toLowerCase();

const isScalar = (value: unknown): boolean =>
  value == null ||
  typeof value === 'string' ||
  typeof value === 'number' ||
  typeof value === 'boolean';

/**
 * The offers collection off the unfiltered menu. The filtered collection the
 * services tab renders from would resolve only the groups currently on screen.
 */
const getOffersCollection = (context: OfferTrackingContext) =>
  context.fullMenu && context.fullMenu.get && context.fullMenu.get('offersCollection');

const getGroupsCollection = (context: OfferTrackingContext) =>
  context.fullMenu && context.fullMenu.get && context.fullMenu.get('groupsCollection');

const getMenuGroupName = (
  groupId: number | null,
  context: OfferTrackingContext,
): string | undefined => {
  const groups = getGroupsCollection(context);
  const group = groupId != null && groups && groups.get && groups.get(groupId);
  return (group && group.get('name')) || undefined;
};

/**
 * `treatment_type` is the Treatment ("Ladies - Wet Haircut"); `treatment_category`
 * is the treatment type it belongs to ("Ladies Haircuts"), which the treatments
 * collection calls `treatmentGroupId`. Both are Treatwell-wide taxonomy strings,
 * identical across every venue, which is what makes them worth grouping on.
 */
const getTreatmentNames = (
  treatmentId: number | null,
  context: OfferTrackingContext,
): { treatment_type?: string; treatment_category?: string } => {
  const treatments = context.treatments;
  const treatmentsCollection =
    treatments && treatments.get && treatments.get('treatmentsCollection');
  const treatment =
    treatmentId != null &&
    treatmentsCollection &&
    treatmentsCollection.get &&
    treatmentsCollection.get(treatmentId);

  if (!treatment) {
    return {};
  }

  const typesCollection = treatments.get('treatmentTypesCollection');
  const type =
    typesCollection &&
    typesCollection.get &&
    typesCollection.get(treatment.get('treatmentGroupId'));

  return {
    treatment_type: treatment.get('name') || undefined,
    treatment_category: (type && type.get('name')) || undefined,
  };
};

/**
 * One pricing option as a partner sees it on the row: its name, full and sale
 * price, duration and, when priced per level, the pricing level's name
 * (`employeeCategoryId` in the model). Every key is always present, as `null`
 * when the route or the row has no value for it, so a list-of-objects property
 * keeps one shape across services, packages and both creation routes.
 */
// A type alias rather than an interface: only an alias is assignable to the
// index-signature object type `track()` accepts.
type PricingOption = {
  name: string | null;
  price: number | null;
  sale_price: number | null;
  duration_mins: number | null;
  pricing_level: string | null;
};

const getPricingLevelName = (
  employeeCategoryId: number | null | undefined,
  context: OfferTrackingContext,
): string | null => {
  const categories = context.employeeCategories;
  const category =
    employeeCategoryId != null &&
    categories &&
    categories.get &&
    categories.get(employeeCategoryId);
  return (category && category.get('name')) || null;
};

const pricingOptionSortKey = (option: PricingOption): string =>
  JSON.stringify([
    option.pricing_level,
    option.name,
    option.duration_mins,
    option.price,
    option.sale_price,
  ]);

/**
 * Sorted on content rather than kept in model order: the list is diffed as a
 * whole, and the SKUs the server hands back after a save need not come in the
 * order the dialog loaded them — a reorder nobody made would report as an edit.
 */
const sortPricingOptions = (options: PricingOption[]): PricingOption[] =>
  options.slice().sort((left, right) => {
    const leftKey = pricingOptionSortKey(left);
    const rightKey = pricingOptionSortKey(right);
    if (leftKey === rightKey) {
      return 0;
    }
    return leftKey < rightKey ? -1 : 1;
  });

/**
 * A package row keeps almost nothing of its own: it is a combination of the
 * included services' options (`subSkus`). The dialog labels it from their
 * names, prices it from their sum unless the partner set a custom price, and
 * lets the partner switch combinations off. These mirror that, so the event
 * describes the rows the partner actually sees (THY-2397 follow-up).
 */
const hasMatchingSubSkuNames = (subSkus: OfferSku[] | undefined): boolean => {
  const named = (subSkus || []).filter((subSku) => subSku.name);
  return (
    named.length >= 2 &&
    named.every(
      (subSku) => (subSku.name as string).toUpperCase() === (named[0].name as string).toUpperCase(),
    )
  );
};

/**
 * Whether a row is offered. A saved row carries `visible`; a row the dialog has
 * not stored a choice for gets the same default the dialog renders
 * (`PackagePricing.getServicesAndSkus`): when some rows pair options of the same
 * name ("Short hair" + "Short hair"), only those rows start switched on. Both
 * sides of an edit go through this, so a default the dialog merely wrote down
 * on save does not read as the partner switching rows off.
 */
const isPackageRowOffered = (sku: OfferSku, skus: OfferSku[]): boolean => {
  if (sku.visible != null) {
    return Boolean(sku.visible);
  }
  return skus.some((row) => hasMatchingSubSkuNames(row.subSkus))
    ? hasMatchingSubSkuNames(sku.subSkus)
    : true;
};

/**
 * One pricing row, described once: the option as reported, plus the pricing
 * level id, which `employee_category_count` counts. Every SKU summary on the
 * event is derived from these, so for a package the counts, ranges and
 * `pricing_options` can never disagree about what the rows cost.
 */
interface PricingRow {
  option: PricingOption;
  employeeCategoryId: number | null;
}

const describeServiceRows = (skus: OfferSku[], context: OfferTrackingContext): PricingRow[] =>
  skus.map((sku) => ({
    option: {
      name: (sku.name && sku.name.trim()) || null,
      price: toFiniteNumber(sku.fullPriceAmount),
      // An empty sale-price field submits `""`; no sale price either way.
      sale_price: toFiniteNumber(sku.discountPriceAmount),
      duration_mins: toFiniteNumber(sku.duration),
      pricing_level: getPricingLevelName(sku.employeeCategoryId, context),
    },
    employeeCategoryId: sku.employeeCategoryId ?? null,
  }));

const describePackageRows = (
  skus: OfferSku[],
  model: OfferModelLike,
  context: OfferTrackingContext,
): PricingRow[] => {
  const isCustom = isPackagePricingCustom(skus);
  // Options shared by every row say nothing about which row this is, so the
  // dialog leaves them out of the label — and so does this.
  const subSkuIdsIgnoredForName = findRepeatingSubSkuIdsInAllSkus(
    skus.map((sku) => ({ ...sku, subSkus: sku.subSkus || [] })),
  );
  const subServices = model.get('subServices') || [];

  return skus
    .filter((sku) => isPackageRowOffered(sku, skus))
    .map((sku) => {
      const subSkus = sku.subSkus || [];
      const levelSubSku = subSkus.find((subSku) => (subSku.employeeCategoryId || 0) > 0);
      const employeeCategoryId =
        sku.employeeCategoryId || (levelSubSku && levelSubSku.employeeCategoryId) || null;
      const subSkuDuration = subSkus.reduce(
        (total, subSku) => total + (toFiniteNumber(subSku.duration) || 0),
        0,
      );

      return {
        employeeCategoryId,
        option: {
          name: getPackageRowName(subSkus, subSkuIdsIgnoredForName, subServices) || null,
          // The sum the dialog shows, sale prices included, unless the partner
          // typed a price for the package.
          price: isCustom
            ? toFiniteNumber(sku.fullPriceAmount)
            : subSkus.length > 0
            ? calculatePrice(subSkus)
            : null,
          // A package has no sale price of its own; its options' sale prices are
          // already in the summed price.
          sale_price: null,
          duration_mins:
            toFiniteNumber(sku.duration) || (subSkus.length > 0 ? subSkuDuration : null),
          pricing_level: getPricingLevelName(employeeCategoryId, context),
        },
      };
    });
};

/**
 * The model's `isPackagePricingCustom()` test — the first row carries a price of
 * its own — read off the rows being described rather than the model, so the
 * before and after sides of an edit each answer for themselves.
 */
const isPackagePricingCustom = (skus: OfferSku[]): boolean =>
  skus.length > 0 ? skus[0].fullPriceAmount != null : false;

/**
 * The taxonomy names of everything a package bundles, deduped. Same vocabulary
 * as `treatment_type` on service events, so packages and services can be broken
 * down on one axis. `treatment_count` already carries the true size.
 */
const getPackageTreatmentTypes = (
  model: OfferModelLike,
  context: OfferTrackingContext,
): string[] => {
  const offersCollection = getOffersCollection(context);
  // `getTreatmentIds` walks `subServices` unguarded, and the whole tracker is
  // wrapped in `withErrorHandling` — a package saved without that attribute
  // would lose its entire event, not just this property.
  if (!model.getTreatmentIds || !offersCollection || !Array.isArray(model.get('subServices'))) {
    return [];
  }

  const treatmentIds: number[] = model.getTreatmentIds(offersCollection) || [];

  return treatmentIds
    .map((treatmentId) => getTreatmentNames(treatmentId, context).treatment_type)
    .filter((name): name is string => !!name)
    .filter((name, index, all) => all.indexOf(name) === index);
};

/**
 * Attributes the partner cannot meaningfully be said to have "edited", even
 * though the model saves them. `description`, `restrictions` and `goodToKnow`
 * are HTML that comes back from the server re-serialised, so they report a
 * change on every save of an untouched offer; the rest are bookkeeping.
 */
const OFFER_ATTRIBUTE_DENYLIST = [
  'id',
  'venueId',
  'pending',
  'hadFreeSkuWhenLoaded',
  'description',
  'restrictions',
  'goodToKnow',
  'images',
  'supplierReference',
  // Structures with no scalar reading a report could use.
  'fulfillmentTypes',
  'pricing',
];

/**
 * How each saved attribute becomes reportable properties. Array-of-object
 * attributes have to reduce to scalars — `old_values: { skus: [...] }` is both
 * unreportable and fat — and ids become the names they stand for.
 *
 * Anything in `attributesToSave()` without an entry here falls through to its
 * snake_cased name when it is scalar, so a field added to the model later is
 * picked up rather than silently dropped.
 */
const OFFER_ATTRIBUTE_PROJECTIONS: Record<
  string,
  (value: any, model: OfferModelLike, context: OfferTrackingContext) => Record<string, unknown>
> = {
  name: (value, model) =>
    getMenuItemEntity(model) === 'package'
      ? { package_name: value || '' }
      : { treatment_type_custom_name: value || '' },

  groupId: (value, model, context) => {
    const menuGroupId = toFiniteNumber(value) ?? 0;
    const menuGroupName = getMenuGroupName(menuGroupId, context);

    return {
      menu_group_id: menuGroupId,
      ...(menuGroupName ? { menu_group_name: menuGroupName } : {}),
    };
  },

  primaryTreatmentId: (value, model, context) => getTreatmentNames(toFiniteNumber(value), context),

  visible: (value) => ({ is_visible: Boolean(value) }),
  listed: (value) => ({ is_listed: Boolean(value) }),
  featured: (value) => ({ is_featured: Boolean(value) }),
  requiresPatchTest: (value) => ({ requires_patch_test: Boolean(value) }),
  discountRuleId: (value) => ({ has_discount_rule: value != null }),

  // The form's colour field is empty for an offer that has never had a colour,
  // and submits that as `""` where the loaded model holds `null` — the same
  // "no colour", reported as a change on every save (THY-2397 QA).
  connectDisplayColour: (value) => ({ connect_display_colour: value || null }),

  skus: (value: OfferSku[], model, context) => {
    const skus = value || [];
    const isPackage = getMenuItemEntity(model) === 'package';
    // A package row keeps almost nothing of its own, so a package is described
    // from its offered rows as the dialog prices them; a service from its SKUs.
    const rows = isPackage
      ? describePackageRows(skus, model, context)
      : describeServiceRows(skus, context);
    const prices = rows
      .map((row) => row.option.price)
      .filter((price): price is number => price != null);
    const durations = rows
      .map((row) => row.option.duration_mins)
      .filter((duration): duration is number => duration != null);

    return {
      sku_count: rows.length,
      employee_category_count: countUnique(rows.map((row) => row.employeeCategoryId)),
      // Min and max alone cannot see a change to a middle SKU — repricing one of
      // three employee categories would report no edit at all — so the sorted
      // rows travel with them.
      sku_prices: prices.slice().sort((left, right) => left - right),
      sku_durations: durations.slice().sort((left, right) => left - right),
      // The rows themselves (THY-2397 follow-up), for reports that break down by
      // option or pricing level rather than by the price range alone.
      pricing_options: sortPricingOptions(rows.map((row) => row.option)),
      ...(isPackage
        ? { package_pricing: isPackagePricingCustom(skus) ? 'custom' : 'services_sum' }
        : {}),
      // Absent rather than zero when the offer has no priced SKUs at all, so a
      // genuinely free service is not indistinguishable from missing data.
      ...(prices.length > 0
        ? { price_min: Math.min(...prices), price_max: Math.max(...prices) }
        : {}),
      ...(durations.length > 0
        ? { duration_min: Math.min(...durations), duration_max: Math.max(...durations) }
        : {}),
    };
  },

  subServices: (value, model, context) => ({
    sub_service_count: (value || []).length,
    ...(getMenuItemEntity(model) === 'package'
      ? { treatment_types: getPackageTreatmentTypes(model, context) }
      : {}),
  }),

  treatmentIds: (value) => ({ treatment_count: (value || []).length }),
  employees: (value) => ({ employee_count: (value || []).length }),
  distributionChannels: (value) => ({ distribution_channel_count: (value || []).length }),
  // The days only restrict bookings while `useApplicableDays` is on. With it off
  // the form still ticks the venue's own days and submits all of them, where the
  // loaded model holds none — so the raw count went 0 → 7 on every save of an
  // offer that never restricted its days (THY-2397 QA). Off means no restriction,
  // which is 0 on both sides.
  applicableDays: (value, model) => ({
    applicable_day_count: model.get('useApplicableDays') ? (value || []).length : 0,
  }),
};

/**
 * Every property of an offer that can be compared between two points in time.
 * Built from the model's own `attributesToSave()` so it cannot drift from what
 * a partner is actually able to change.
 */
const buildOfferComparable = (
  model: OfferModelLike,
  context: OfferTrackingContext,
): TrackingProperties => {
  const attributes = (model.attributesToSave && model.attributesToSave()) || [];
  const comparable: TrackingProperties = {};

  attributes.forEach((attribute) => {
    if (OFFER_ATTRIBUTE_DENYLIST.indexOf(attribute) !== -1) {
      return;
    }

    const value = model.get(attribute);
    const projection = OFFER_ATTRIBUTE_PROJECTIONS[attribute];

    if (projection) {
      Object.assign(comparable, projection(value, model, context));
    } else if (isScalar(value)) {
      comparable[toSnakeCase(attribute)] = value as TrackingProperties[string];
    }
  });

  return comparable;
};

const buildOfferProperties = (model: OfferModelLike, comparable: TrackingProperties) => ({
  ...comparable,
  entity_type: getMenuItemEntity(model),
  offer_id: toFiniteNumber(model.id) ?? 0,
  menu_item_type_code: model.get('menuItemTypeCode') || '',
  // The dialog's own notion of how the offer is priced (simple, by employee
  // category, custom, 2d) — already computed for the legacy event on save.
  pricing_type: (model.getPricingType && model.getPricingType()) || '',
  currency_code: getCurrencyCode(),
});

const isSameValue = (left: unknown, right: unknown): boolean => {
  if (Array.isArray(left) || Array.isArray(right)) {
    return JSON.stringify(left) === JSON.stringify(right);
  }
  return left === right;
};

/**
 * What changed between two comparable maps, in the shape THY-2397 asked for:
 * an `edited` list of field names plus the before and after of those fields
 * only. A field that appears on one side and not the other counts as edited.
 */
export const buildEditDiff = (
  before: TrackingProperties | undefined,
  after: TrackingProperties,
): TrackingProperties => {
  if (!before) {
    return {};
  }

  const keys = Object.keys(after)
    .concat(Object.keys(before))
    .filter((key, index, all) => all.indexOf(key) === index);

  const edited = keys.filter((key) => !isSameValue(before[key], after[key]));

  if (edited.length === 0) {
    return { edited: [] as string[] };
  }

  const oldValues: TrackingProperties = {};
  const newValues: TrackingProperties = {};

  edited.forEach((key) => {
    // `undefined` would be dropped on the way to Segment, leaving `edited` naming
    // a field that never appears in `old_values`.
    oldValues[key] = before[key] === undefined ? null : before[key];
    newValues[key] = after[key] === undefined ? null : after[key];
  });

  return { edited, old_values: oldValues, new_values: newValues };
};

/**
 * The offer as it was when the dialog finished loading. Held in a WeakMap
 * rather than on the model so that nothing analytics-shaped ends up in the
 * attributes the dialog saves, and so it is collected with the model.
 */
const offerSnapshots = new WeakMap<object, TrackingProperties>();

/**
 * Called by the offer dialog once `fetch()` has resolved, next to the existing
 * `hadFreeSkuWhenLoaded` snapshot. Everything an edit is diffed against is
 * captured here — by the time the save runs, the form has already overwritten
 * the model in a single `set()`.
 */
export const captureOfferSnapshot = withErrorHandling(
  ({ model, ...context }: { model: OfferModelLike } & OfferTrackingContext) => {
    // The dialog can render more than once for the same model; a later capture
    // would snapshot the half-edited offer and report no edit at all.
    if (offerSnapshots.has(model)) {
      return;
    }
    offerSnapshots.set(model, buildOfferComparable(model, context));
  },
);

/**
 * Fired when the dialog for a *new* service or package opens. The ticket only
 * asks for an add_new intent event, so an edit opening the same dialog fires
 * nothing — see the open question on the parent ticket about edit flows having
 * no denominator.
 *
 * In practice only packages reach this: `OfferTypeChoiceDialog` routes the
 * `treatment` choice to `AddServicesModal` and only the other types to
 * `MenuOffer2`. The service half of the pair is fired by the two functions at
 * the bottom of this file. Kept general anyway — the offer dialog is still
 * reachable with a treatment model through the legacy per-group flow.
 */
export const trackMenuItemAddInitiated = withErrorHandling((model: OfferModelLike) => {
  track(MENU_ITEM_INITIATED_EVENTS[getMenuItemEntity(model)], {
    menu_group_id: toFiniteNumber(model.get('groupId')) ?? 0,
  });
});

/**
 * Fired once the offer save has come back successfully. `archive` wins over
 * `edit` when the partner used the Archive button: server-side both are the
 * same PUT with visible=false, so the operation is only knowable here.
 *
 * An edit also reports what changed, against the snapshot taken when the dialog
 * loaded. A create has nothing to diff against, and an archive is one button
 * rather than a set of edits, so both report the offer alone.
 */
export const trackMenuItemSaved = withErrorHandling(
  ({
    model,
    isNew,
    isArchive,
    ...context
  }: {
    model: OfferModelLike;
    isNew: boolean;
    isArchive: boolean;
  } & OfferTrackingContext) => {
    const operation: SaveOperation = isArchive ? 'archive' : isNew ? 'add_new' : 'edit';
    const comparable = buildOfferComparable(model, context);

    track(MENU_ITEM_COMPLETED_EVENTS[getMenuItemEntity(model)][operation], {
      ...buildOfferProperties(model, comparable),
      ...(operation === 'edit' ? buildEditDiff(offerSnapshots.get(model), comparable) : {}),
      operation,
    });
  },
);

export const trackMenuGroupAddInitiated = withErrorHandling(() => {
  track(MENU_GROUP_INITIATED_EVENT);
});

interface MenuGroupLike {
  name?: string | null;
  description?: { content?: unknown } | string | null;
  active?: boolean;
}

/**
 * Descriptions are edited in a Draft.js editor whose `onChange` fires on
 * selection as well as typing, and which re-serialises the whole document each
 * time — so clicking into the field and changing nothing rewrites the markup.
 * Comparing (and reporting) the text alone is what makes a description edit
 * mean an edit. This is the same problem `description` is denylisted for on
 * offers; here the text is worth reporting, so it is normalised instead.
 */
const toPlainText = (html: string): string =>
  html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * The description arrives as `{ content, contentType }` from the API and as a
 * plain string from the modal, so both shapes reduce to the text itself.
 */
const getMenuGroupDescription = (description: MenuGroupLike['description']): string => {
  if (typeof description === 'string') {
    return toPlainText(description);
  }
  const content = description && description.content;
  return typeof content === 'string' ? toPlainText(content) : '';
};

const buildMenuGroupComparable = (group: MenuGroupLike | undefined) => ({
  menu_group_name: (group && group.name) || '',
  menu_group_description: getMenuGroupDescription(group && group.description),
  // In the comparable rather than added at the end: unarchiving a group is an
  // edit in which nothing but this changes, and it would otherwise report an
  // empty `edited`.
  is_active: Boolean(group && group.active),
});

/**
 * The group as the modal opened it. Held here rather than read from the store:
 * `state.menu.menuGroup` is only ever written by `getMenuGroupSuccess`, which
 * nothing dispatches — the modal receives the group as props from the services
 * tab — so selecting it returned the reducer's initial state and reported every
 * save as a rename from an empty name (THY-2397).
 *
 * A single value rather than a map: one group modal is open at a time.
 */
let openMenuGroupSnapshot: MenuGroupLike | undefined;

export const captureMenuGroupSnapshot = withErrorHandling((group?: MenuGroupLike) => {
  openMenuGroupSnapshot = group;
});

/**
 * Fired from the saga that owns the API call, like the discount events below —
 * the modal reports at submit time, which would count a server-rejected save as
 * completed and could never carry a newly created group's id.
 */
export const trackMenuGroupSaved = withErrorHandling(
  ({
    menuGroupId,
    operation,
    isActive,
    group,
  }: {
    menuGroupId?: number | null;
    operation: MenuGroupOperation;
    isActive: boolean;
    group?: MenuGroupLike;
  }) => {
    // A delete submits no body, so the group the modal opened with is the only
    // place its name can come from.
    const snapshot = openMenuGroupSnapshot;
    const comparable = buildMenuGroupComparable(group || snapshot);

    track(MENU_GROUP_COMPLETED_EVENTS[operation], {
      entity_type: 'menu_group',
      menu_group_id: toFiniteNumber(menuGroupId) ?? 0,
      ...comparable,
      is_active: isActive,
      ...(operation === 'edit'
        ? buildEditDiff(snapshot && buildMenuGroupComparable(snapshot), comparable)
        : {}),
      operation,
    });

    // The modal is closing; a snapshot left behind would attach to whichever
    // group opens next, and the delete path has no other source for the name.
    openMenuGroupSnapshot = undefined;
  },
);

export const trackPricingLevelAddInitiated = withErrorHandling(() => {
  track(PRICING_LEVEL_INITIATED_EVENT);
});

export const trackDiscountAddInitiated = withErrorHandling(() => {
  track(DISCOUNT_INITIATED_EVENT);
});

/**
 * The discount rule as the dialog sends it to the API — `name`, `active`, one
 * `lastMinuteDiscount` object, one `offpeakDiscounts` object and the ids of the
 * assigned menu items.
 *
 * This shape is why THY-2397 exists: the first version of this module read
 * `lastMinuteDiscounts`, `offPeakDiscounts` and `treatmentIds`, none of which
 * the dialog has ever sent, so three properties reported 0 on every event. The
 * unit test agreed with the code because its fixture was written from the code.
 */
interface LastMinuteDiscountLike {
  active?: boolean;
  percentage?: number | string | null;
  maxHoursBeforeAppointment?: number | string | null;
}

interface OffPeakDiscountsLike {
  active?: boolean;
  days?: Record<string, Record<string, unknown>>;
}

interface DiscountRuleLike {
  name?: string | null;
  active?: boolean;
  lastMinuteDiscount?: LastMinuteDiscountLike;
  offpeakDiscounts?: OffPeakDiscountsLike;
  assignedMenuItemIds?: unknown[];
}

/**
 * The rule as the API returns it, which is what `state.menuDiscount.discountRules`
 * holds — raw, before `DiscountsTab/container.ts` runs it through the transformers
 * that the dialog's props are built from. The lead time is
 * `applicableHoursBeforeAppointment` here and assignments are whole menu items,
 * so an edit diff normalises this shape before comparing it against the one the
 * dialog submits; comparing them raw would report untouched fields as changed.
 */
interface StoredDiscountRuleLike {
  name?: string | null;
  active?: boolean;
  lastMinuteDiscount?: {
    active?: boolean;
    percentage?: number | string | null;
    applicableHoursBeforeAppointment?: number | string | null;
  };
  offpeakDiscounts?: OffPeakDiscountsLike;
  assignedMenuItems?: { id?: number }[];
}

const fromStoredDiscountRule = (stored: StoredDiscountRuleLike): DiscountRuleLike => {
  const lastMinute = stored.lastMinuteDiscount || {};

  return {
    name: stored.name,
    active: stored.active,
    lastMinuteDiscount: {
      active: lastMinute.active,
      percentage: lastMinute.percentage,
      maxHoursBeforeAppointment: lastMinute.applicableHoursBeforeAppointment,
    },
    offpeakDiscounts: stored.offpeakDiscounts,
    assignedMenuItemIds: (stored.assignedMenuItems || []).map((item) => item && item.id),
  };
};

/**
 * Off-peak discounts are a map of weekday to a map of period to percentage, so
 * a day counts once it carries any percentage at all, and the range across all
 * of them is what a partner would recognise as "the off-peak discount".
 */
const buildOffPeakProperties = (offPeak: OffPeakDiscountsLike | undefined) => {
  // The dialog submits a bare `{ active: false }` when the partner switches off-peak
  // off, while the stored rule keeps its old day map — so reading the map of an
  // inactive rule would report days and percentages nobody touched.
  if (!(offPeak && offPeak.active)) {
    return { off_peak_active: false, off_peak_day_count: 0 };
  }

  const days = offPeak.days || {};
  const percentages: number[] = [];
  let dayCount = 0;

  Object.keys(days).forEach((day) => {
    const periods = days[day] || {};
    const dayPercentages = Object.keys(periods)
      .map((period) => toFiniteNumber(periods[period]))
      .filter((percentage): percentage is number => percentage != null && percentage > 0);

    if (dayPercentages.length > 0) {
      dayCount += 1;
    }

    percentages.push(...dayPercentages);
  });

  return {
    off_peak_active: true,
    off_peak_day_count: dayCount,
    ...(percentages.length > 0
      ? {
          off_peak_percentage_min: Math.min(...percentages),
          off_peak_percentage_max: Math.max(...percentages),
        }
      : {}),
  };
};

const buildDiscountComparable = (rule: DiscountRuleLike | undefined) => {
  const lastMinute = (rule && rule.lastMinuteDiscount) || {};
  // Same asymmetry as off-peak: the submitted rule drops percentage and lead time
  // when last-minute is off, the stored one keeps them.
  const isLastMinuteActive = Boolean(lastMinute.active);

  return {
    discount_name: (rule && rule.name) || '',
    is_active: Boolean(rule && rule.active),
    last_minute_active: isLastMinuteActive,
    ...(isLastMinuteActive && toFiniteNumber(lastMinute.percentage) != null
      ? { last_minute_percentage: toFiniteNumber(lastMinute.percentage) }
      : {}),
    ...buildOffPeakProperties(rule && rule.offpeakDiscounts),
    assigned_menu_item_count: ((rule && rule.assignedMenuItemIds) || []).length,
  };
};

/**
 * The lead-time window is reported but never diffed. The dialog is not given
 * the stored `applicableHoursBeforeAppointment` — no transformer maps it onto
 * the `maxHoursBeforeAppointment` prop it reads — so `SelectDropdown` falls back
 * to its first option and the save submits that whether or not the partner
 * touched the dropdown. Diffing it would report a lead-time change on every
 * edit of a last-minute rule. The underlying round-trip bug is the dialog's,
 * not this module's, and is filed separately (THY-2397 review).
 */
const buildLastMinuteWindow = (rule: DiscountRuleLike | undefined) => {
  const hours = toFiniteNumber(
    rule && rule.lastMinuteDiscount && rule.lastMinuteDiscount.maxHoursBeforeAppointment,
  );
  return hours != null ? { last_minute_max_hours_before: hours } : {};
};

/**
 * Fired from the saga that owns the API call, not the dialog: delete has no
 * dialog of its own, and all three operations then report from one place. A
 * delete carries no rule body, so the saga reads the rule being removed out of
 * the store to report its name.
 */
export const trackDiscountSaved = withErrorHandling(
  ({
    operation,
    discountId,
    rule,
    storedRule,
  }: {
    operation: DiscountOperation;
    discountId?: number | null;
    rule?: DiscountRuleLike;
    storedRule?: StoredDiscountRuleLike;
  }) => {
    const previousRule = storedRule && fromStoredDiscountRule(storedRule);
    // A delete submits no body, so the rule as the store last saw it is all
    // there is to report — including the name the partner removed.
    const comparable = buildDiscountComparable(rule || previousRule);

    track(DISCOUNT_COMPLETED_EVENTS[operation], {
      entity_type: 'discount',
      ...comparable,
      ...buildLastMinuteWindow(rule || previousRule),
      discount_id: toFiniteNumber(discountId) ?? 0,
      ...(operation === 'edit'
        ? buildEditDiff(previousRule && buildDiscountComparable(previousRule), comparable)
        : {}),
      operation,
    });
  },
);

/**
 * Services are not created through the offer dialog. `OfferTypeChoiceDialog`
 * sends the `treatment` choice to `AddServicesModal`, a React modal that adds
 * many services from templates in one POST to `CREATE_SERVICES_FROM_TEMPLATE`,
 * so both service events are fired from there instead of `menu-offer2.js`.
 */

interface TemplateServiceSku {
  name?: string | null;
  durationTimeMins?: number | null;
  fullPriceAmount?: number | string | null;
}

interface TemplateService {
  serviceId?: number | null;
  name?: string | null;
  employees?: number[];
  skus?: TemplateServiceSku[];
}

interface TemplateServiceGroup {
  menuGroupId?: number | null;
  name?: string | null;
  services?: TemplateService[];
}

export const trackServiceAddInitiated = withErrorHandling(
  ({ menuGroupId }: { menuGroupId?: number | null }) => {
    track(MENU_ITEM_INITIATED_EVENTS.service, {
      menu_group_id: toFiniteNumber(menuGroupId) ?? 0,
    });
  },
);

/**
 * One `add_new.service.completed` per service created, so the event still
 * counts services the way the offer-dialog path does rather than counting
 * submissions. `batch_size` and `created_via` are what let a dashboard separate
 * the two routes; without them a 30-service bulk add is indistinguishable from
 * 30 separate saves.
 *
 * This route has no client-side link to the treatment taxonomy — the server
 * maps templates to Treatments — so `treatment_type` is the template's own name
 * and `treatment_category` is left out rather than guessed at. `created_via`
 * marks the events where that difference applies (THY-2397).
 *
 * Properties the bulk endpoint cannot supply are left out rather than sent as
 * zero: it returns no offer ids, and visibility, featured, patch-test and
 * discount-rule flags are all server defaults the modal never shows.
 */
export const trackServicesCreatedFromTemplate = withErrorHandling(
  ({
    menuGroups,
    targetMenuGroupId,
    targetMenuGroupName,
    templateServiceNames,
  }: {
    menuGroups: TemplateServiceGroup[];
    targetMenuGroupId?: number | null;
    targetMenuGroupName?: string;
    templateServiceNames?: Record<number, string>;
  }) => {
    const batchSize = menuGroups.reduce(
      (total, group) => total + ((group && group.services) || []).length,
      0,
    );
    const currencyCode = getCurrencyCode();

    menuGroups.forEach((group) => {
      const menuGroupId = toFiniteNumber(targetMenuGroupId ?? (group && group.menuGroupId)) ?? 0;
      // Opened from the tab rather than from inside a group there is no target,
      // but each submitted group carries its own name.
      const menuGroupName = targetMenuGroupName || (group && group.name) || '';

      ((group && group.services) || []).forEach((service) => {
        const skus = (service && service.skus) || [];
        const prices = skus
          .map((sku) => toFiniteNumber(sku.fullPriceAmount))
          .filter((price): price is number => price != null);
        const durations = skus
          .map((sku) => toFiniteNumber(sku.durationTimeMins))
          .filter((duration): duration is number => duration != null);
        const serviceId = toFiniteNumber(service && service.serviceId);
        const templateName =
          serviceId != null && templateServiceNames ? templateServiceNames[serviceId] : undefined;

        track(MENU_ITEM_COMPLETED_EVENTS.service.add_new, {
          entity_type: 'service',
          operation: 'add_new',
          menu_group_id: menuGroupId,
          ...(menuGroupName ? { menu_group_name: menuGroupName } : {}),
          menu_item_type_code: 'T',
          // The template's own name is the closest this route has to the
          // taxonomy; the partner may have renamed it before submitting.
          ...(templateName ? { treatment_type: templateName } : {}),
          treatment_type_custom_name: (service && service.name) || '',
          sku_count: skus.length,
          ...(prices.length > 0
            ? { price_min: Math.min(...prices), price_max: Math.max(...prices) }
            : {}),
          ...(durations.length > 0
            ? { duration_min: Math.min(...durations), duration_max: Math.max(...durations) }
            : {}),
          // Same shape as the offer dialog's. The template modal has no sale
          // price or pricing levels, so those two are always null here.
          pricing_options: sortPricingOptions(
            skus.map((sku) => ({
              name: (sku.name && sku.name.trim()) || null,
              price: toFiniteNumber(sku.fullPriceAmount),
              sale_price: null,
              duration_mins: toFiniteNumber(sku.durationTimeMins),
              pricing_level: null,
            })),
          ),
          currency_code: currencyCode,
          employee_count: ((service && service.employees) || []).length,
          created_via: 'services_template_modal',
          batch_size: batchSize,
        });
      });
    });
  },
);
